import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import { Injector } from "@/components/form/Injector";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { AnonymousContentWrapper } from "@/components/wrappers/AnonymousContentWrapper";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { useCountdown } from "@/utils/hooksUtils";
import { openUrl } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Button, Checkbox, Col, Divider, Form, Input, Row, Select, Space, type InputRef } from "antd";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useLogin, useSendTotpCodeViaSms, useVerifyTotpCode } from "../../../common/api/queries";
import { SMS_TOTP_CODE_RESEND_DELAY_SECONDS } from "../../../common/constants";
import { Role } from "../../../common/security/enums";
import { CURRENT_USER_ROUTE_PATHS, USER_ROUTE_PATHS } from "../../user/paths";
import { startSession } from "../session";
import { getAuthSnapshot } from "../store";
import { AuthenticatedLoginResponse, LoginBaseRequest, VerifyTotpCodeRequest } from "../types";
import { isAuthenticatedLoginResponse } from "../utils";

type LoginFormValues = LoginBaseRequest & VerifyTotpCodeRequest;

const SMS_TOTP_DEVICE_ID = "SMS";

export const LoginContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const [count, startCountdown] = useCountdown();

  const smsTotpCodeInputRef = useRef<InputRef>(null);

  const [form] = Form.useForm<LoginFormValues>();
  const totpDeviceId = Form.useWatch("totpDeviceId", form);

  const login = useLogin();
  const sendTotpCodeViaSms = useSendTotpCodeViaSms();
  const verifyTotpCode = useVerifyTotpCode();

  const totpChallenge = login.data && !isAuthenticatedLoginResponse(login.data) ? login.data : undefined;
  const totpDevices = totpChallenge?.totpDevices ?? [];

  const handleAuthenticated = (response: AuthenticatedLoginResponse) => {
    startSession(response);

    // Only the agent portal lets the user add a TOTP device.
    if (getAuthSnapshot().selectedAccount.role === Role.AGENT && !response.totpDevices?.length) {
      modal.warning({
        title: t("login.titles.totpDeviceNotFound"),
        content: t("login.helpers.totpDeviceRecommendation"),
        okText: t("login.actions.addTotpDeviceNow"),
        closable: true,
        mask: { closable: false },
        onOk: () =>
          openUrl(
            window.location.origin + generatePath(CURRENT_USER_ROUTE_PATHS.profile.to) + "?addTotpDevice=true",
            "_blank",
          ),
      });
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (totpChallenge) {
          verifyTotpCode.mutate(
            {
              email: values.email,
              totpDeviceId: values.totpDeviceId === SMS_TOTP_DEVICE_ID ? undefined : values.totpDeviceId,
              totpCode: values.totpCode,
              rememberMe: values.rememberMe,
            },
            {
              onSuccess: handleAuthenticated,
              onError: (error) => setErrorsToForm(form, "login.attrs", error.violations),
            },
          );
        } else {
          login.mutate(
            { email: values.email, password: values.password, rememberMeToken: getAuthSnapshot().rememberMe?.token },
            {
              onSuccess: (response) => {
                if (isAuthenticatedLoginResponse(response)) {
                  handleAuthenticated(response);
                }
              },
              onError: (error) => setErrorsToForm(form, "login.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  const handleSendTotpCodeViaSmsClick = () => {
    const { email, password } = form.getFieldsValue();

    sendTotpCodeViaSms.mutate(
      { email, password },
      {
        onSuccess: () => {
          startCountdown(SMS_TOTP_CODE_RESEND_DELAY_SECONDS);
          smsTotpCodeInputRef.current?.focus();
        },
      },
    );
  };

  // The next login may offer other devices, so the TOTP fields start over.
  const handleBackToLoginClick = () => {
    login.reset();
    form.resetFields(["totpDeviceId", "totpCode"]);
  };

  return (
    <AnonymousContentWrapper>
      <h2 className="center-align margin-bottom-large">
        {totpChallenge ? t("login.titles.totpCodeForm") : t("login.titles.loginForm")}
      </h2>

      <Row justify="center">
        <Col style={{ flex: "0 0 400px" }}>
          <Form form={form} layout="vertical" name="loginForm">
            {totpChallenge ? (
              <>
                <HiddenInput name="email" />
                <HiddenInput name="password" />

                <Form.Item
                  name="totpDeviceId"
                  label={t("login.attrs.totpDeviceId")}
                  rules={[validations.notNull]}
                  initialValue={totpDevices[0]?.id ?? SMS_TOTP_DEVICE_ID}
                  className="left-align"
                >
                  <Select
                    options={[
                      ...totpDevices.map((device) => ({
                        value: device.id,
                        label: (
                          <>
                            <AntIcon type="mobile" /> <Divider orientation="vertical" /> {device.name}
                          </>
                        ),
                      })),
                      {
                        value: SMS_TOTP_DEVICE_ID,
                        label: (
                          <>
                            <AntIcon type="message" /> <Divider orientation="vertical" />{" "}
                            {t("login.helpers.sms") + " (" + totpChallenge.maskedUserPhone + ")"}
                          </>
                        ),
                      },
                    ]}
                  />
                </Form.Item>

                {totpDeviceId !== SMS_TOTP_DEVICE_ID ? (
                  <Form.Item
                    name="totpCode"
                    label={t("login.attrs.totpCode")}
                    rules={[validations.notBlank, validations.length(6), validations.numeric]}
                  >
                    <Input autoFocus prefix={<AntIcon type="lock" className="icon-black-opacity" />} />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="totpCode"
                    label={t("login.attrs.totpCode")}
                    rules={[validations.notBlank, validations.length(6), validations.numeric]}
                  >
                    <Injector>
                      {(props) => (
                        <Space.Compact style={{ width: "100%" }}>
                          <SecondaryButton
                            onClick={handleSendTotpCodeViaSmsClick}
                            icon={!count && <AntIcon type="send" />}
                            disabled={!!count}
                            loading={sendTotpCodeViaSms.isPending}
                          >
                            {!!count && `(${count}) `}
                            {t("user.actions.sendTotpCodeViaSms")}
                          </SecondaryButton>

                          <Input
                            ref={smsTotpCodeInputRef}
                            autoFocus
                            prefix={<AntIcon type="lock" className="icon-black-opacity" />}
                            {...props}
                          />
                        </Space.Compact>
                      )}
                    </Injector>
                  </Form.Item>
                )}

                <Form.Item
                  name="rememberMe"
                  className="form-item-without-label"
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue
                >
                  <Checkbox>{t("login.attrs.rememberMe")}</Checkbox>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    onClick={handleFormSubmit}
                    loading={verifyTotpCode.isPending}
                  >
                    {t("login.actions.verifyTotpCode")}
                  </Button>
                </Form.Item>

                <div className="center-align margin-top-medium">
                  <ActionTextIcon
                    color="blue"
                    icon="arrow-left"
                    text={t("user.actions.backToLogin")}
                    onClick={handleBackToLoginClick}
                  />
                </div>
              </>
            ) : (
              <>
                <Form.Item
                  name="email"
                  label={t("login.attrs.email")}
                  rules={[validations.notBlank, validations.size(1, 254), validations.email]}
                >
                  <Input autoFocus prefix={<AntIcon type="user" className="icon-black-opacity" />} />
                </Form.Item>

                <Form.Item name="password" label={t("login.attrs.password")} rules={[validations.notBlank]}>
                  <Input.Password prefix={<AntIcon type="lock" className="icon-black-opacity" />} />
                </Form.Item>

                <Form.Item>
                  <Button
                    className="margin-top-medium"
                    type="primary"
                    htmlType="submit"
                    block
                    onClick={handleFormSubmit}
                    loading={login.isPending}
                  >
                    {t("login.actions.loginSubmit")}
                  </Button>

                  <div className="center-align margin-top-medium">
                    <Link to={USER_ROUTE_PATHS.requestPasswordReset.to}>{t("login.actions.forgottenPassword")}</Link>
                  </div>
                </Form.Item>
              </>
            )}
          </Form>
        </Col>
      </Row>
    </AnonymousContentWrapper>
  );
};
